import type { ReactElement, ReactNode } from 'react'
import {
  I18nextProvider,
  useTranslation as _useTranslation,
  initReactI18next
} from 'react-i18next'

import { logError } from '@ecomm/error-handling'
import type { Locale } from '@ecomm/utils/locale/types'
import type { InitOptions } from 'i18next'
import { createInstance } from 'i18next'
import type { i18n } from 'i18next'
import type i18next from 'i18next'
import { resources } from './resources'

const initOptions: InitOptions = {
  fallbackLng: 'en-US',
  debug: process.env['NODE_ENV'] === 'development',
  // I18n will default to the first namespace listed here if a defaultNs is not specified.
  // If we don't specify an ns array nor a defaultNs, it will default to "translation"
  ns: ['translation', 'contact', 'common', 'shop.smb', 'shop'],
  interpolation: {
    escapeValue: false
  },
  supportedLngs: ['en-US', 'en-GB'],
  initAsync: false
}

/*
 * Creates a new i18n instance that we can pass down to I18NProvider
 */
const createI18nInstance = () => createInstance()

/*
 * Initializes an existing i18n instance with the provided locale and our default config options
 */
const initializeI18nInstance = (i18next: i18n, lng: Locale) =>
  i18next.use(initReactI18next).init({
    ...initOptions,
    lng,
    resources: {
      [lng]: resources[lng]
    },
    react: {
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['p', 'h3', 'a']
    }
  })

/*
 * Creates and initializes a new i18n instance with the provided locale
 */
const createAndInitializeI18n = (lng: Locale) => {
  const i18next = createI18nInstance()

  // the initialization is async and we don't need to wait for it to finish
  initializeI18nInstance(i18next, lng)
    .then(() => i18next)
    .catch((e: unknown) => {
      return e instanceof Error
        ? logError(e, {
            errorCategory: 'i18n'
          })
        : logError(new Error('Failed to initialize i18n'), {
            errorCategory: 'i18n',
            error: JSON.stringify(e)
          })
    })

  return i18next
}

declare module 'i18next' {
  interface CustomTypeOptions {
    readonly resources: (typeof resources)['en-US']
  }
}

type I18nProviderProps = {
  readonly children: ReactNode
  readonly i18nInstance: typeof i18next
}

const I18nProvider = ({
  children,
  i18nInstance
}: I18nProviderProps): ReactElement => {
  return <I18nextProvider i18n={i18nInstance}>{children}</I18nextProvider>
}

export {
  createI18nInstance,
  initializeI18nInstance,
  createAndInitializeI18n,
  resources,
  I18nProvider
}

/**
 * A hook to access values stored in i18n resource files.
 *
 * ## Best practices
 * Never use duplicated values in i18n files.
 * `en-GB` will fallback to `en-US` values and vice versa.
 * You do not need to add in empty strings or copy and paste values.
 *
 * If you need to reference the same value in multiple locations, you can use i18n's nested functionality: https://www.i18next.com/translation-function/nesting
 *
 * All text inside of JSX should come from i18n. This is enforced by a linting rule.
 */
export const useTranslation = _useTranslation

export { Trans } from 'react-i18next'
